import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { type PieceWithoutId } from '../types/Piece.js'
import { type PieceTimeIndication } from '../types/PieceTimeIndication.js'
import { usePieceStatusDescription } from './usePieceStatusDescription.js'
import { useDaysAgoIndication } from './useDaysAgoIndication.js'
import { useTimeIndicationDescription } from './useTimeIndicationDescription.js'

export type PieceTimeIndicationDisplayFn = (piece: PieceWithoutId) => string | undefined | null

export const usePieceTimeIndication = (): PieceTimeIndicationDisplayFn => {
  const { t } = useTranslation()
  const getStatusDescription = usePieceStatusDescription()
  const getDaysAgoIndication = useDaysAgoIndication()
  const getTimeIndicationDescription = useTimeIndicationDescription()

  const timeIndicationAvailable = (timeInd?: PieceTimeIndication): string | undefined => {
    switch (timeInd?.indicationType) {
      case 'MomentIndication':
        return timeInd?.moment
      case 'IntervalIndication':
        return timeInd?.start
      default:
        return undefined
    }
  }

  return useCallback<PieceTimeIndicationDisplayFn>(
    (piece) => {
      const { returnedDaysAgo, isReturn, receivingTimeIndication, undeliverableSinceDaysAgo, isUndeliverable, status } =
        piece

      if (isReturn && returnedDaysAgo != null) {
        return getDaysAgoIndication(returnedDaysAgo, 'trackTrace.RETURNED')
      }

      if (isUndeliverable) {
        return getDaysAgoIndication(undeliverableSinceDaysAgo, 'trackTrace.RETURNED')
      }

      if (timeIndicationAvailable(receivingTimeIndication)) {
        return getTimeIndicationDescription(receivingTimeIndication)
      }

      if (status) {
        return getStatusDescription(piece)
      }

      return t('trackTrace.stateMessage.DELIVERY_AS_SOON')
    },
    [getDaysAgoIndication, getStatusDescription, getTimeIndicationDescription, t]
  )
}
